/*
 * @Author: mina.min
 * @Date: 2025-01-20 10:00:00
 * @LastEditors: mina.min
 * @LastEditTime: 2025-06-21 18:58:58
 * @FilePath: /OTAManagementSystem/src/store/firmwareStore.ts
 * @Description: 固件管理数据存储
 */
import authStore from '../stores/authStore'

export interface FirmwarePackage {
  id: string
  moduleId: string
  moduleName: string
  version: string
  fileName: string
  fileSize: number
  fileUrl: string
  checksum: string
  releaseNotes: string
  status: 'Draft' | 'Published'
  isHidden?: boolean // 是否为隐藏固件
  createdAt: string
  updatedAt?: string
  modifiedBy: string
}

export interface OTAUpgradeItem {
  id: string
  moduleName: string
  currentVersion: string
  upgradeVersion: string
  order: number
}

// 车辆升级状态接口
export interface VehicleUpgradeStatus {
  id: string
  vehicleId: string
  batch: string
  description: string
  overallProgress: number // 总体升级进度
  status: 'Pending' | 'Upgrading' | 'Success' | 'Failed'
  moduleProgresses: ModuleProgress[] // 各个模块的升级进度
  startTime?: string
  endTime?: string
}

// 模块升级进度接口
export interface ModuleProgress {
  moduleId: string
  moduleName: string
  currentVersion: string
  upgradeVersion: string
  progress: number // 该模块的升级进度
  status: 'Pending' | 'Upgrading' | 'Success' | 'Failed'
  startTime?: string
  endTime?: string
  errorMessage?: string
}

// 模拟固件数据
const MOCK_FIRMWARE_DATA: FirmwarePackage[] = [
  {
    id: '1',
    moduleId: '1',
    moduleName: 'ECU控制模块',
    version: '1.2.0',
    fileName: 'ecu_firmware_v1.2.0.bin',
    fileSize: 2048576,
    fileUrl: '/firmware/ecu_firmware_v1.2.0.bin',
    checksum: 'sha256:abc123...',
    releaseNotes: '修复了启动延迟问题，优化了燃油效率算法',
    status: 'Published',
    createdAt: '2024-01-15 10:30:00',
    updatedAt: '2024-01-16 09:00:00',
    modifiedBy: 'admin',
  },
  {
    id: '2',
    moduleId: '1',
    moduleName: 'ECU控制模块',
    version: '1.1.0',
    fileName: 'ecu_firmware_v1.1.0.bin',
    fileSize: 2015232,
    fileUrl: '/firmware/ecu_firmware_v1.1.0.bin',
    checksum: 'sha256:abc111...',
    releaseNotes: '优化了怠速控制逻辑',
    status: 'Published',
    isHidden: true, // 隐藏固件示例
    createdAt: '2024-01-10 14:20:00',
    updatedAt: '2024-01-11 10:00:00',
    modifiedBy: 'developer',
  },
  {
    id: '3',
    moduleId: '2',
    moduleName: 'TCU变速箱模块',
    version: '2.3.0',
    fileName: 'tcu_firmware_v2.3.0.bin',
    fileSize: 1536000,
    fileUrl: '/firmware/tcu_firmware_v2.3.0.bin',
    checksum: 'sha256:def456...',
    releaseNotes: '增加了新的换挡逻辑，提升了换挡平顺性',
    status: 'Published',
    createdAt: '2024-01-16 14:20:00',
    updatedAt: '2024-01-17 10:00:00',
    modifiedBy: 'developer',
  },
  {
    id: '4',
    moduleId: '3',
    moduleName: 'BCM车身模块',
    version: '1.6.0',
    fileName: 'bcm_firmware_v1.6.0.bin',
    fileSize: 1024000,
    fileUrl: '/firmware/bcm_firmware_v1.6.0.bin',
    checksum: 'sha256:ghi789...',
    releaseNotes: '修复了车窗控制bug，增加了新的防盗功能',
    status: 'Draft',
    isHidden: true, // 隐藏固件示例
    createdAt: '2024-01-08 16:45:00',
    updatedAt: '2024-01-09 08:30:00',
    modifiedBy: 'developer',
  },
]

// 模拟OTA升级模块数据
const MOCK_OTA_UPGRADE_DATA: OTAUpgradeItem[] = [
  {
    id: '1',
    moduleName: 'ECU控制模块',
    currentVersion: '1.0.0',
    upgradeVersion: '1.2.0',
    order: 1,
  },
  {
    id: '2',
    moduleName: 'TCU变速箱模块',
    currentVersion: '2.1.0',
    upgradeVersion: '2.3.0',
    order: 2,
  },
  {
    id: '3',
    moduleName: 'BCM车身模块',
    currentVersion: '1.5.0',
    upgradeVersion: '1.6.0',
    order: 3,
  },
]

// 模拟车辆升级状态数据
let MOCK_VEHICLE_UPGRADE_STATUS: VehicleUpgradeStatus[] = []

// 模拟API延迟
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// 模拟API响应格式
interface ApiResponse<T> {
  code: number
  message: string
  data: T
}

// 固件管理API
export const firmwareApi = {
  // 获取固件列表
  async getFirmwareList(): Promise<ApiResponse<FirmwarePackage[]>> {
    await delay(500) // 模拟网络延迟
    return {
      code: 200,
      message: '获取成功',
      data: [...MOCK_FIRMWARE_DATA],
    }
  },

  // 添加固件包
  async addFirmware(
    firmware: Omit<FirmwarePackage, 'id' | 'createdAt' | 'modifiedBy'>
  ): Promise<ApiResponse<FirmwarePackage>> {
    await delay(300)
    const newFirmware: FirmwarePackage = {
      id: Date.now().toString(),
      ...firmware,
      createdAt: new Date().toLocaleString(),
      updatedAt: firmware.status === 'Published' ? new Date().toLocaleString() : undefined,
      modifiedBy: authStore.user?.username || 'unknown',
    }
    MOCK_FIRMWARE_DATA.push(newFirmware)
    return {
      code: 200,
      message: '添加成功',
      data: newFirmware,
    }
  },

  // 更新固件包
  async updateFirmware(
    id: string,
    firmware: Partial<FirmwarePackage>
  ): Promise<ApiResponse<FirmwarePackage>> {
    await delay(300)
    const index = MOCK_FIRMWARE_DATA.findIndex(item => item.id === id)
    if (index === -1) {
      return {
        code: 404,
        message: '固件包不存在',
        data: null as any,
      }
    }
    MOCK_FIRMWARE_DATA[index] = {
      ...MOCK_FIRMWARE_DATA[index],
      ...firmware,
      updatedAt: new Date().toLocaleString(),
      modifiedBy: authStore.user?.username || 'unknown',
    }
    return {
      code: 200,
      message: '更新成功',
      data: MOCK_FIRMWARE_DATA[index],
    }
  },

  // 删除固件包
  async deleteFirmware(id: string): Promise<ApiResponse<null>> {
    await delay(200)
    const index = MOCK_FIRMWARE_DATA.findIndex(item => item.id === id)
    if (index === -1) {
      return {
        code: 404,
        message: '固件包不存在',
        data: null,
      }
    }
    MOCK_FIRMWARE_DATA.splice(index, 1)
    return {
      code: 200,
      message: '删除成功',
      data: null,
    }
  },

  // 发布固件包
  async publishFirmware(id: string): Promise<ApiResponse<FirmwarePackage>> {
    await delay(300)
    const index = MOCK_FIRMWARE_DATA.findIndex(item => item.id === id)
    if (index === -1) {
      return {
        code: 404,
        message: '固件包不存在',
        data: null as any,
      }
    }
    MOCK_FIRMWARE_DATA[index] = {
      ...MOCK_FIRMWARE_DATA[index],
      status: 'Published',
      updatedAt: new Date().toLocaleString(),
      modifiedBy: authStore.user?.username || 'unknown',
    }
    return {
      code: 200,
      message: '发布成功',
      data: MOCK_FIRMWARE_DATA[index],
    }
  },

  // 获取已发布的固件列表（用于OTA升级选择）
  async getPublishedFirmwareList(): Promise<ApiResponse<FirmwarePackage[]>> {
    await delay(300)
    const publishedFirmware = MOCK_FIRMWARE_DATA.filter(item => item.status === 'Published')
    return {
      code: 200,
      message: '获取成功',
      data: publishedFirmware,
    }
  },
}

// OTA升级管理API
export const otaApi = {
  // 获取OTA升级模块列表
  async getOTAUpgradeList(): Promise<ApiResponse<OTAUpgradeItem[]>> {
    await delay(400)
    return {
      code: 200,
      message: '获取成功',
      data: [...MOCK_OTA_UPGRADE_DATA],
    }
  },

  // 添加升级模块
  async addOTAUpgradeItem(
    item: Omit<OTAUpgradeItem, 'id' | 'order'>
  ): Promise<ApiResponse<OTAUpgradeItem>> {
    await delay(300)
    const newItem: OTAUpgradeItem = {
      id: Date.now().toString(),
      ...item,
      order: MOCK_OTA_UPGRADE_DATA.length + 1,
    }
    MOCK_OTA_UPGRADE_DATA.push(newItem)
    return {
      code: 200,
      message: '添加成功',
      data: newItem,
    }
  },

  // 更新升级模块
  async updateOTAUpgradeItem(
    id: string,
    item: Partial<OTAUpgradeItem>
  ): Promise<ApiResponse<OTAUpgradeItem>> {
    await delay(300)
    const index = MOCK_OTA_UPGRADE_DATA.findIndex(upgrade => upgrade.id === id)
    if (index === -1) {
      return {
        code: 404,
        message: '升级项不存在',
        data: null as any,
      }
    }
    MOCK_OTA_UPGRADE_DATA[index] = { ...MOCK_OTA_UPGRADE_DATA[index], ...item }
    return {
      code: 200,
      message: '更新成功',
      data: MOCK_OTA_UPGRADE_DATA[index],
    }
  },

  // 删除升级模块
  async deleteOTAUpgradeItem(id: string): Promise<ApiResponse<null>> {
    await delay(200)
    const index = MOCK_OTA_UPGRADE_DATA.findIndex(item => item.id === id)
    if (index === -1) {
      return {
        code: 404,
        message: '升级项不存在',
        data: null,
      }
    }
    MOCK_OTA_UPGRADE_DATA.splice(index, 1)
    // 重新排序
    MOCK_OTA_UPGRADE_DATA.forEach((item, idx) => {
      item.order = idx + 1
    })
    return {
      code: 200,
      message: '删除成功',
      data: null,
    }
  },

  // 更新升级模块顺序
  async updateOTAUpgradeOrder(items: OTAUpgradeItem[]): Promise<ApiResponse<OTAUpgradeItem[]>> {
    await delay(200)
    // 清空原数据并重新设置
    MOCK_OTA_UPGRADE_DATA.length = 0
    MOCK_OTA_UPGRADE_DATA.push(...items)
    return {
      code: 200,
      message: '排序更新成功',
      data: [...MOCK_OTA_UPGRADE_DATA],
    }
  },

  // 获取车辆升级状态列表
  async getVehicleUpgradeStatus(): Promise<ApiResponse<VehicleUpgradeStatus[]>> {
    await delay(300)
    return {
      code: 200,
      message: '获取成功',
      data: [...MOCK_VEHICLE_UPGRADE_STATUS],
    }
  },

  // 重试车辆升级
  async retryVehicleUpgrade(vehicleId: string): Promise<ApiResponse<VehicleUpgradeStatus>> {
    await delay(300)
    const index = MOCK_VEHICLE_UPGRADE_STATUS.findIndex(item => item.id === vehicleId)
    if (index === -1) {
      return {
        code: 404,
        message: '车辆不存在',
        data: null as any,
      }
    }

    MOCK_VEHICLE_UPGRADE_STATUS[index] = {
      ...MOCK_VEHICLE_UPGRADE_STATUS[index],
      status: 'Pending',
      overallProgress: 0,
      moduleProgresses: MOCK_VEHICLE_UPGRADE_STATUS[index].moduleProgresses.map(module => ({
        ...module,
        progress: 0,
        status: 'Pending' as const,
        startTime: undefined,
        endTime: undefined,
        errorMessage: undefined,
      })),
      startTime: new Date().toLocaleString(),
      endTime: undefined,
    }

    return {
      code: 200,
      message: '重试升级已开始',
      data: MOCK_VEHICLE_UPGRADE_STATUS[index],
    }
  },

  // 开始OTA升级
  async startOTAUpgrade(upgradeData: {
    modules: Array<{
      id: string
      moduleName: string
      currentVersion: string
      upgradeVersion: string
      order: number
    }>
    vehicles: Array<{
      id: string
      vehicleId: string
      batch: string
      description: string
    }>
  }): Promise<ApiResponse<VehicleUpgradeStatus[]>> {
    await delay(500)

    console.log('开始OTA升级，升级数据：', upgradeData)
    console.log(`升级模块数量：${upgradeData.modules.length}`)
    console.log(`目标车辆数量：${upgradeData.vehicles.length}`)

    // 初始化车辆升级状态
    MOCK_VEHICLE_UPGRADE_STATUS = upgradeData.vehicles.map(vehicle => ({
      id: vehicle.id,
      vehicleId: vehicle.vehicleId,
      batch: vehicle.batch,
      description: vehicle.description,
      overallProgress: 0,
      status: 'Pending' as const,
      moduleProgresses: upgradeData.modules.map(module => ({
        moduleId: module.id,
        moduleName: module.moduleName,
        currentVersion: module.currentVersion,
        upgradeVersion: module.upgradeVersion,
        progress: 0,
        status: 'Pending' as const,
      })),
      startTime: new Date().toLocaleString(),
    }))

    return {
      code: 200,
      message: 'OTA升级已开始',
      data: [...MOCK_VEHICLE_UPGRADE_STATUS],
    }
  },
}
